import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {
  AssetType,
  useGetAssetQuery,
  useUpsertAssetLocationsMutation,
  useUpsertAssetMutation,
} from "../store/slices/mapsSlice";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import AssetLocations from "./AssetLocations";
import { Tab, Tabs } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AssetContacts from "./AssetContacts";
import AssetRelations from "./AssetRelations";
import { toast } from "react-toastify";
import AssetLocationsList from "./AssetLocationsList";
import { makeid } from "../helpers";
import { getOverlayLocation } from "../Maps/helpers";
import StyledTabContainer from "../Common/StyledTabContainer";
import {
  formatAssetKindOptionLabel,
  getAssetKindValueLabels,
  loadAssetKindOptions,
} from "./assetIconsList";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import { PlaceAutocomplete } from "../Contacts/PlacesSearch";
import { APIProvider } from "@vis.gl/react-google-maps";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";
import HelpButton from "../HelpContent/HelpButton";
import { useConfirmDialog } from "../Builder/Components/ConfirmDialogContext";

export default function EditAssetModal({
  assetUuid,
  onClose,
  tab = "details",
  modalButton,
  forceShow = false,
}: {
  assetUuid: string;
  onClose?: () => void;
  tab?: string;
  modalButton?: React.ReactNode;
  forceShow?: boolean;
}) {
  const navigate = useNavigate();
  const confirmDialog = useConfirmDialog();
  const [show, setShow] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Partial<AssetType>>({});
  const { data: asset } = useGetAssetQuery(assetUuid || "", {
    skip: assetUuid === "new" || !assetUuid || !(show || forceShow),
  });

  useEffect(() => {
    if (assetUuid === "new") {
      setSelectedAsset({
        // Uuid: shortUUID(),
        Label: "New asset",
        HasLocation: false,
        CanMove: false,
        Reference: makeid(8),
      });
      return;
    }
    if (asset) {
      setSelectedAsset(asset);
    }
  }, [asset, assetUuid]);

  const handleClose = () => {
    setSelectedTab("details");
    setShow(false);
    onClose?.();
  };
  const [saveAsset, result] = useUpsertAssetMutation();
  const [saveAssetLocation, resultLocation] = useUpsertAssetLocationsMutation();
  const [locationOverlays, setLocationOverlays] = useState<any>([]);

  const handleSave = async () => {
    const data: any = await saveAsset(selectedAsset);
    if (data.error) {
      console.log(data.error);
      return;
    }
    const r = data.data;

    if (r) {
      toast.success("Asset saved successfully");
      setSelectedAsset(r);

      if (selectedAsset.HasLocation && selectedAsset.Uuid) {
        const body = locationOverlays
          .filter((o: any) => !o.clusterData || !o.clusterData.IsCluster)
          .map((o: any) => ({
            ...getOverlayLocation(o),
          }));
        const res: any = await saveAssetLocation({
          assetUuid: selectedAsset.Uuid || "",
          body,
        });
        if (res.error) {
          console.log(res.error);
          return;
        }
        // remove overlays
        locationOverlays.forEach((o: any) => {
          o.setMap(null);
        });
        setLocationOverlays([]);

        toast.success("Asset location saved successfully");
      }
      // setSelectedTab("details");

      // navigate(`/assets/assets/${r.Uuid}`, { replace: true });
    }
  };

  const [selectedTab, setSelectedTab] = useState(tab);

  return (
    <>
      {!forceShow && (
        <button
          className="btn p-0 bg-transparent m-0"
          onClick={() => setShow(true)}
        >
          {modalButton}
        </button>
      )}
      <Modal show={show || forceShow} onHide={handleClose} size="xl">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex justify-content-between flex-grow-1">
            <div>
              {(assetUuid === "new" ? "Add Asset - " : "Edit Asset - ") +
                selectedAsset.Label}
              <HelpButton helpKey="edit-asset" />
            </div>
            <div className="fs-6"> Ref:{selectedAsset.Uuid}</div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <StyledTabContainer id="edit-asset-tabs">
            <Tabs
              mountOnEnter
              activeKey={selectedTab}
              onSelect={(k: any) => setSelectedTab(k)}
            >
              <Tab
                title={
                  <>
                    <span>Asset details</span>
                    <HelpButton helpKey="asset-details" />
                  </>
                }
                eventKey="details"
              >
                <div className="p-3 d-flex flex-column" style={{ height: 700 }}>
                  <div className="flex-grow-1">
                    <div className="row">
                      <div className="col-12 mb-3 text-secondary">
                        Add the details of the Asset you wish to add or edit.
                      </div>
                      <div className="col-lg-6 mb-3">
                        <Input
                          label="Asset Name"
                          value={[selectedAsset?.Label]}
                          onChange={([v]: any) =>
                            setSelectedAsset({ ...selectedAsset, Label: v })
                          }
                          options={undefined}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <Input
                          label="Asset Type"
                          value={[selectedAsset?.Kind]}
                          onChange={([v]: any) =>
                            setSelectedAsset({ ...selectedAsset, Kind: v })
                          }
                          inputType="select"
                          isValidNewOption={(v) => v.trim().length > 0}
                          options={undefined}
                          placeholder="Select or type to add new"
                          loadOptions={loadAssetKindOptions as any}
                          formatOptionLabel={formatAssetKindOptionLabel}
                          getValueLabels={getAssetKindValueLabels as any}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <Input
                          label="Description"
                          inputType="textArea"
                          value={[selectedAsset?.Description]}
                          onChange={([v]: any) =>
                            setSelectedAsset({
                              ...selectedAsset,
                              Description: v,
                            })
                          }
                          options={undefined}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <Input
                          label="Reference"
                          value={[selectedAsset?.Reference]}
                          onChange={([v]: any) =>
                            setSelectedAsset({ ...selectedAsset, Reference: v })
                          }
                          options={undefined}
                        />
                        <span className="text-secondary small">
                          You can change the reference, if you wish to use a
                          custom one. It must be unique to this asset.
                        </span>
                      </div>

                      <div className="col-lg-6 mb-3">
                        <InputLabel>Search address</InputLabel>
                        <APIProvider
                          apiKey={(window as any).GM_API_KEY}
                          version="3.64"
                        >
                          <PlaceAutocomplete
                            onPlaceSelect={async (place) => {
                              setSelectedAsset({
                                ...selectedAsset,
                                Address: place?.formatted_address || "",
                              });
                              if (
                await confirmDialog(
                                  "Add location?",
                                  "Would you like to add a location for this asset?"
                                )
                              ) {
                                if (
                                  !place?.geometry?.location?.lng() ||
                                  !place?.geometry?.location?.lat()
                                ) {
                                  toast.error(
                                    "Selected place does not have a valid location."
                                  );
                                  return;
                                }

                                const assetResponse: any = await saveAsset({
                                  ...selectedAsset,
                                  Address: place?.formatted_address || "",
                                  HasLocation: true,
                                });

                                if (assetResponse.data?.Uuid) {
                                  setSelectedAsset(assetResponse.data);

                                  const res: any = await saveAssetLocation({
                                    assetUuid: assetResponse.data.Uuid || "",
                                    body: [
                                      {
                                        Point: {
                                          type: "Point",
                                          coordinates: [
                                            place?.geometry?.location?.lng(),
                                            place?.geometry?.location?.lat(),
                                          ],
                                        },
                                        Description:
                                          place?.formatted_address || "",
                                        IsCurrent: false,
                                        IsShape: false,
                                      },
                                    ],
                                  });
                                  if (res.error) {
                                    console.log(res.error);
                                    return;
                                  }
                                  toast.success(
                                    "Asset location saved successfully"
                                  );
                                }
                              }
                            }}
                            className="form-control p-2"
                          />
                        </APIProvider>
                        <Input
                          className="mt-3"
                          label="Enter address manually"
                          inputType="textArea"
                          value={[selectedAsset?.Address]}
                          onChange={([v]: any) =>
                            setSelectedAsset({ ...selectedAsset, Address: v })
                          }
                          options={undefined}
                        />
                      </div>
                      <div className="col-lg-4 mb-3 justify-content-between d-flex">
                        <Input
                          label="Has location?"
                          value={[!!selectedAsset?.HasLocation]}
                          onChange={([v]: any) =>
                            setSelectedAsset({
                              ...selectedAsset,
                              HasLocation: v,
                            })
                          }
                          inputType="radio"
                          radioName="hasLocation"
                          options={[
                            { label: "No", value: false },
                            { label: "Yes", value: true },
                          ]}
                        />{" "}
                        {/* <Input
                        label="Can move?"
                        value={[!!selectedAsset?.CanMove]}
                        onChange={([v]: any) =>
                          setSelectedAsset({ ...selectedAsset, CanMove: v })
                        }
                        inputType="radio"
                        radioName="canMove"
                        options={[
                          { label: "No", value: false },
                          { label: "Yes", value: true },
                        ]}
                      /> */}
                      </div>
                    </div>
                  </div>
                  {!selectedAsset?.Uuid && (
                    <div className="alert alert-secondary">
                      Please save the asset to see more options.
                    </div>
                  )}
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="colored"
                      className="mt-3"
                      onClick={() => {
                        handleSave();
                      }}
                    >
                      Save asset
                    </Button>
                  </div>
                </div>
              </Tab>
              {selectedAsset.Uuid && selectedAsset.HasLocation && (
                <Tab
                  title={
                    <>
                      <span>Asset locations on map </span>
                      <HelpButton helpKey="asset-locations-on-map" />
                    </>
                  }
                  eventKey="locations"
                >
                  <div className="p-3" style={{ height: 700 }}>
                    <AssetLocations
                      asset={selectedAsset}
                      setLocationOverlays={setLocationOverlays}
                      onSave={() => {
                        handleSave();
                      }}
                    />
                  </div>
                </Tab>
              )}
              {selectedAsset.Uuid && selectedAsset.HasLocation && (
                <Tab
                  title={
                    <>
                      <span>Asset locations list </span>
                      <HelpButton helpKey="asset-locations-list" />
                    </>
                  }
                  eventKey="locationsList"
                >
                  <div className="p-3" style={{ minHeight: 700 }}>
                    <AssetLocationsList selectedAsset={selectedAsset} />
                  </div>
                </Tab>
              )}
              {selectedAsset.Uuid && (
                <Tab
                  title={
                    <>
                      <span>Asset contacts </span>
                      <HelpButton helpKey="asset-contacts" />
                    </>
                  }
                  eventKey="contacts"
                >
                  <div className="" style={{ minHeight: 700 }}>
                    <AssetContacts selectedAsset={selectedAsset} />
                  </div>
                </Tab>
              )}
              {selectedAsset.Uuid && (
                <Tab
                  title={
                    <>
                      <span>Asset relations </span>
                      <HelpButton helpKey="asset-relations" />
                    </>
                  }
                  eventKey="relations"
                >
                  <div className="" style={{ minHeight: 700 }}>
                    <AssetRelations selectedAsset={selectedAsset} />
                  </div>
                </Tab>
              )}
            </Tabs>
          </StyledTabContainer>
          <JsonCollapse json={selectedAsset} />
        </Modal.Body>
      </Modal>
    </>
  );
}
